import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authedFetch } from '../hooks/useAuth';

const DONATION_EMAIL = import.meta.env.VITE_DONATION_EMAIL ?? 'support@my-investments.io';

type PickStats = {
  total_picks: number;
  total_with_results: number;
  avg_matches: number;
  best_pick: {
    pick_id: string;
    main_matches: number;
    bonus_match: boolean;
    prize_tier: string;
    game: string;
    draw_date: string;
  } | null;
};

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState<PickStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setStatsLoading(true);
    authedFetch('/api/v1/picks/stats')
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { setStats(d); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, [user]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><p className="text-slate-500">Loading…</p></div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg text-center">
          <p className="mb-4 text-slate-600">You need to sign in to view your profile.</p>
          <Link to="/login" className="rounded-full bg-sky-600 px-6 py-2 font-semibold text-white">Sign in</Link>
        </div>
      </div>
    );
  }

  function handleLogout() {
    logout();
    nav('/');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="mx-auto max-w-lg">
        <Link to="/" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Back to picks</Link>

        {user.jackpot_winner && (
          <div className="mb-6 rounded-2xl border border-yellow-300 bg-yellow-50 p-5">
            <p className="text-lg font-bold text-yellow-800">Congratulations! You're a jackpot winner!</p>
            <p className="mt-1 text-sm text-yellow-700">
              As per the platform Terms & Conditions you agreed to on registration, please consider a 10% donation of
              your winnings. Thank you for using AI Lotto Predictor.
            </p>
            <a
              href={`mailto:${DONATION_EMAIL}?subject=Jackpot%20Donation`}
              className="mt-3 inline-block rounded-full bg-yellow-500 px-5 py-2 text-sm font-semibold text-white hover:bg-yellow-600"
            >
              Donate now
            </a>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{user.email}</h1>
              <p className="text-sm text-slate-500">Member since {new Date(user.created_at).toLocaleDateString('en-ZA')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="rounded-full border border-slate-200 px-4 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${user.is_verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {user.is_verified ? 'Email verified' : 'Email unverified'}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${user.totp_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
              {user.totp_enabled ? '2FA enabled' : '2FA disabled'}
            </span>
          </div>

          {!user.totp_enabled && (
            <Link
              to="/profile/2fa"
              className="mt-3 block text-sm font-medium text-sky-600 hover:text-sky-800"
            >
              Enable two-factor authentication →
            </Link>
          )}
        </div>

        {!statsLoading && stats && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-2xl font-bold text-slate-900">{stats.total_picks}</p>
              <p className="text-xs text-slate-500">Picks saved</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-2xl font-bold text-slate-900">{stats.total_with_results}</p>
              <p className="text-xs text-slate-500">Results evaluated</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-2xl font-bold text-slate-900">{stats.avg_matches.toFixed(1)}</p>
              <p className="text-xs text-slate-500">Avg matches</p>
            </div>
            {stats.best_pick && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm">
                <p className="text-lg font-bold text-green-800">{stats.best_pick.prize_tier}</p>
                <p className="text-xs text-green-600">{stats.best_pick.game} · {stats.best_pick.draw_date}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex gap-3">
          <Link
            to="/profile/picks"
            className="flex-1 rounded-full bg-sky-600 py-3 text-center font-semibold text-white shadow-sm transition hover:bg-sky-700"
          >
            View pick history
          </Link>
          <Link
            to="/leaderboard"
            className="flex-1 rounded-full border border-slate-300 py-3 text-center text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Leaderboard
          </Link>
        </div>
      </div>
    </div>
  );
}
